import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'

import axiosClient from '../../api/axiosClient'
import Button from '../../components/Button/Button'
import Typography from '../../components/Typography'
import RectifyInvoiceModal from '../../components/Modal/RectifyInvoiceModal'
import { theme } from '../../theme'
import {
  formatDateTime,
  ALERT_TYPE_LABELS,
  ALERT_STATUS_LABELS
} from './format'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendientes' },
  { value: 'acknowledged', label: 'En revisión' },
  { value: 'resolved', label: 'Resueltas' },
  { value: 'dismissed', label: 'Descartadas' },
  { value: '', label: 'Todas' }
]

const TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'refund_on_invoiced_payment', label: 'Refund sobre factura' },
  { value: 'invoice_emission_failed', label: 'Emisión fallida' },
  { value: 'pdf_generation_failed', label: 'PDF fallido' },
  { value: 'email_delivery_failed', label: 'Envío email fallido' }
]

const AdminInvoiceAlerts = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ status: 'pending', type: '' })
  // For "Resolve with rectificativa": we need to load the original invoice
  // so the modal can pre-populate lines from it.
  const [rectifyContext, setRectifyContext] = useState(null) // { alert, originalInvoice }

  const fetchAlerts = useCallback(async ({ status, type }) => {
    setLoading(true)
    try {
      const params = {}
      if (status) params.status = status
      if (type) params.type = type
      const { data } = await axiosClient.get('/api/admin/invoice-alerts', { params })
      setItems(data.data.items || [])
    } catch (err) {
      console.error('[AdminInvoiceAlerts] failed', err)
      toast.error('No pudimos cargar las alertas.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAlerts(filters)
  }, [fetchAlerts, filters])

  const transition = async (alertId, action, body = {}) => {
    try {
      await axiosClient.post(`/api/admin/invoice-alerts/${alertId}/${action}`, body)
      toast.success('Alerta actualizada.')
      fetchAlerts(filters)
    } catch (err) {
      console.error('[AdminInvoiceAlerts] transition failed', err)
      const apiError = err?.response?.data
      toast.error(apiError?.message || 'No se pudo actualizar la alerta.')
    }
  }

  const openRectify = async (alert) => {
    if (!alert.invoiceId) {
      toast.error('La alerta no tiene factura asociada.')
      return
    }
    try {
      const invoiceId = alert.invoiceId?._id || alert.invoiceId
      const { data } = await axiosClient.get(`/api/admin/invoices/${invoiceId}`)
      setRectifyContext({ alert, originalInvoice: data.data })
    } catch (err) {
      console.error('[AdminInvoiceAlerts] failed to load original invoice', err)
      toast.error('No pudimos cargar la factura original.')
    }
  }

  // For refund_on_invoiced_payment alerts we pre-fill the rectificativa as
  // a `difference` for the negative refund amount on the matching payment.
  const buildRefundPrefill = (alert, original) => {
    const refundAmount = Number(alert.payload?.refundAmount)
    if (!Number.isFinite(refundAmount) || refundAmount <= 0) return null
    const rawPaymentId = alert.paymentId || original?.lines?.[0]?.paymentId
    const paymentId = rawPaymentId?._id?.toString() || rawPaymentId?.toString() || String(rawPaymentId)
    const refundEuros = refundAmount / 100 // payload.refundAmount is in cents (Stripe)
    return {
      rectificationType: 'difference',
      rectificationReason: `Refund Stripe ${alert.payload?.stripeRefundId || ''}`.trim(),
      lines: [{
        paymentId,
        description: 'Refund parcial/total sobre factura emitida',
        taxableBase: -Math.round(refundEuros * 100) / 100,
        vatRate: original?.vatRate || 21,
        vatAmount: -Math.round((refundEuros * (original?.vatRate || 21) / 121) * 100) / 100
      }]
    }
  }

  return (
    <Container>
      <Header>
        <Typography as="h1" $variant="title-4-semibold">Alertas de facturación</Typography>
      </Header>

      <Filters>
        <FilterField>
          <Label>Estado</Label>
          <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </FilterField>
        <FilterField>
          <Label>Tipo</Label>
          <Select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </FilterField>
      </Filters>

      {loading ? (
        <Empty><Typography as="p">Cargando…</Typography></Empty>
      ) : items.length === 0 ? (
        <Empty><Typography as="p">No hay alertas con esos filtros.</Typography></Empty>
      ) : (
        <AlertList>
          {items.map((alert) => {
            const isOpen = alert.status === 'pending' || alert.status === 'acknowledged'
            return (
              <AlertCard key={alert._id}>
                <AlertHead>
                  <TypeChip $type={alert.type}>{ALERT_TYPE_LABELS[alert.type] || alert.type}</TypeChip>
                  <StatusChip $status={alert.status}>{ALERT_STATUS_LABELS[alert.status] || alert.status}</StatusChip>
                  <CreatedAt>{formatDateTime(alert.createdAt)}</CreatedAt>
                </AlertHead>
                <AlertBody>
                  {alert.invoiceId && (
                    <BodyRow>
                      Factura: <Link to={`/admin/invoices/${alert.invoiceId?._id || alert.invoiceId}`}><Mono>{alert.invoiceId?.invoiceNumber || alert.invoiceId?._id || String(alert.invoiceId)}</Mono></Link>
                    </BodyRow>
                  )}
                  {alert.organizerId && (
                    <BodyRow>Organizador: <Mono>{alert.organizerId?.email || alert.organizerId?._id || String(alert.organizerId)}</Mono></BodyRow>
                  )}
                  {alert.payload && (
                    <Payload>
                      <summary>Detalles</summary>
                      <pre>{JSON.stringify(alert.payload, null, 2)}</pre>
                    </Payload>
                  )}
                  {alert.resolution?.resolvedAt && (
                    <Resolution>
                      <span>Resuelta {formatDateTime(alert.resolution.resolvedAt)}</span>
                      {alert.resolution.rectificativaId && (
                        <> · Rectificativa: <Link to={`/admin/invoices/${alert.resolution.rectificativaId}`}><Mono>{alert.resolution.rectificativaId}</Mono></Link></>
                      )}
                      {alert.resolution.notes && <> · {alert.resolution.notes}</>}
                    </Resolution>
                  )}
                </AlertBody>
                {isOpen && (
                  <AlertActions>
                    {alert.status === 'pending' && (
                      <Button size="sm" $variant="outline" onClick={() => transition(alert._id, 'acknowledge')}>
                        Marcar como vista
                      </Button>
                    )}
                    {alert.invoiceId && (
                      <Button size="sm" onClick={() => openRectify(alert)}>
                        Resolver con rectificativa
                      </Button>
                    )}
                    <Button size="sm" $variant="outline" onClick={() => transition(alert._id, 'dismiss', { notes: 'No requiere acción' })}>
                      Descartar
                    </Button>
                  </AlertActions>
                )}
              </AlertCard>
            )
          })}
        </AlertList>
      )}

      {rectifyContext && (
        <RectifyInvoiceModal
          originalInvoice={rectifyContext.originalInvoice}
          alertId={rectifyContext.alert._id}
          prefill={
            rectifyContext.alert.type === 'refund_on_invoiced_payment'
              ? buildRefundPrefill(rectifyContext.alert, rectifyContext.originalInvoice)
              : null
          }
          onClose={() => setRectifyContext(null)}
          onSuccess={() => {
            setRectifyContext(null)
            fetchAlerts(filters)
          }}
        />
      )}
    </Container>
  )
}

export default AdminInvoiceAlerts

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 220px);
  gap: 12px;
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.defaultStrong};
`

const Select = styled.select`
  height: 40px;
  padding: 0 10px;
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.md};
  background-color: ${theme.fill.defaultMain};
`

const Empty = styled.div`
  padding: 48px 24px;
  border: 1px dashed ${theme.border.defaultMain};
  border-radius: ${theme.radius.md};
  text-align: center;
  color: ${theme.colors.defaultWeak};
`

const AlertList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AlertCard = styled.div`
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.md};
  padding: 16px;
  background-color: ${theme.fill.defaultMain};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const AlertHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const TypeChip = styled.span`
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ $type }) => {
    if ($type === 'refund_on_invoiced_payment') return 'background-color: #fff3cd; color: #856404;'
    if ($type === 'invoice_emission_failed') return 'background-color: #f9d6d6; color: #b71c1c;'
    return 'background-color: #e3f2fd; color: #0d47a1;'
  }}
`

const StatusChip = styled.span`
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ $status }) => {
    if ($status === 'pending') return 'background-color: #fff3cd; color: #856404;'
    if ($status === 'acknowledged') return 'background-color: #e3f2fd; color: #0d47a1;'
    if ($status === 'resolved') return 'background-color: #d6efd5; color: #1b5e20;'
    return 'background-color: #eee; color: #555;'
  }}
`

const CreatedAt = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: ${theme.colors.defaultWeak};
`

const AlertBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
`

const BodyRow = styled.div`
  color: ${theme.colors.defaultStrong};
  a { color: ${theme.colors.brandMain || '#ff5722'}; text-decoration: none; }
`

const Payload = styled.details`
  font-size: 12px;
  summary { cursor: pointer; color: ${theme.colors.defaultWeak}; }
  pre {
    margin: 6px 0 0;
    padding: 8px 12px;
    background-color: ${theme.fill.defaultWeak || '#fafafa'};
    border-radius: ${theme.radius.sm};
    overflow-x: auto;
    max-height: 240px;
  }
`

const Resolution = styled.div`
  padding: 8px 12px;
  background-color: #d6efd5;
  color: #1b5e20;
  border-radius: ${theme.radius.sm};
  font-size: 12px;
  a { color: #0d47a1; text-decoration: none; }
`

const AlertActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
`
